import PropTypes from 'prop-types';

import RecipientSearchPicker from '#contactListFeature/components/SampleRecipientField/RecipientSearchPicker';
import { sanitizeHtml } from '#lib/sanitize';

import { useEmailTemplatePreview } from '../../models/emailTemplates/queries';

/**
 * Where a custom variable occurs, so a missing value can be found in the editor.
 * @param {string} key
 * @param {string} subject
 * @param {string} content
 * @returns {string}
 */
const usedIn = (key, subject, content) => {
	const uses = (text) => text.includes(`{${key}}`) || text.includes(`{${key}|`);
	return [uses(subject) && 'im Betreff', uses(content) && 'im E-Mail-Text'].filter(Boolean).join(' und ');
};

/**
 * The editor's current text as one real recipient would receive it (signature and tracking are
 * added at send time and not shown).
 */
const TemplatePreview = ({ emailSubject, emailContent, recipient, onRecipientChange }) => {
	const { data, isFetching, isError } = useEmailTemplatePreview({ emailSubject, emailContent, recipient });

	return (
		<div className="space-y-4 min-h-[440px]">
			<RecipientSearchPicker recipient={recipient} onChange={onRecipientChange} />

			{!recipient && (
				<div className="rounded-card border border-dashed border-bgBase-500 px-6 py-16 text-center text-sm text-bgBase-800">
					Wählen Sie einen Empfänger, um zu sehen, wie die E-Mail bei ihm ankommt – mit seinen echten Werten
					und eigenen Spalten.
				</div>
			)}

			{recipient && isError && (
				<p className="text-sm text-warning-700">Die Vorschau konnte nicht erstellt werden.</p>
			)}

			{recipient && data && (
				<div className={`rounded-card border border-bgBase-400 bg-white ${isFetching ? 'opacity-60' : ''}`}>
					<div className="border-b border-bgBase-400 px-5 py-3 space-y-1 text-sm">
						<div className="flex gap-2">
							<span className="w-14 text-bgBase-800">An</span>
							<span className="text-text">
								{data.emailRecipient || 'keine E-Mail-Adresse hinterlegt'}
							</span>
						</div>
						<div className="flex gap-2">
							<span className="w-14 text-bgBase-800">Betreff</span>
							<span className="font-semibold text-text">{data.emailSubject || '–'}</span>
						</div>
					</div>
					{data.missingVariables.length > 0 && (
						<div className="mx-5 mt-3 rounded-card bg-caution-200 px-3 py-2 text-xs text-caution-700">
							<p>
								Für diesen Empfänger fehlen Werte – in einer Kampagne würde er übersprungen, außer es
								gibt einen Ersatzwert:
							</p>
							<ul className="mt-1 list-disc pl-4">
								{data.missingVariables.map((variable) => (
									<li key={variable.key}>
										{variable.name} ({usedIn(variable.key, emailSubject, emailContent)})
									</li>
								))}
							</ul>
						</div>
					)}
					<div
						className="rich-text-content px-5 py-4 text-sm text-text min-h-[300px]"
						// eslint-disable-next-line react/no-danger -- rendered template HTML, sanitized
						dangerouslySetInnerHTML={{ __html: sanitizeHtml(data.emailContent) }}
					/>
				</div>
			)}
		</div>
	);
};

TemplatePreview.propTypes = {
	emailSubject: PropTypes.string.isRequired,
	emailContent: PropTypes.string.isRequired,
	recipient: PropTypes.object,
	onRecipientChange: PropTypes.func.isRequired,
};

export default TemplatePreview;
